import React, { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Box,
    CircularProgress,
    Button,
    TextField,
    IconButton,
    Tooltip,
    Stack,
    Checkbox,
    FormControlLabel,
    Alert,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useQuery } from '@tanstack/react-query';
import { fetchDevices, fetchSites, fetchAllDevicesForExport } from '../services/api';
import { Device, FilterParams, ApiResponse, Site } from '../types/api';
import { exportToCSV } from '../utils/csvExport';
import { DeviceDialog } from './DeviceDialog';
import { ExportDialog } from './ExportDialog';


import { DEVICE_TABLE_DEFAULT_FIELDS, DEFAULT_EXPORT_DEVICE_IGNORE_FIELDS } from '../constants/deviceFields';

export const DeviceTable: React.FC = () => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(1000);
    const [filters, setFilters] = useState<FilterParams>({});
    const [searchComputerName, setSearchComputerName] = useState('');
    const [searchSerialNumber, setSearchSerialNumber] = useState('');
    const [searchLastUser, setSearchLastUser] = useState('');
    const [isExporting, setIsExporting] = useState(false);
    const [copiedField, setCopiedField] = useState<string | null>(null);
    const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [pulsewayMissing, setPulsewayMissing] = useState<boolean | null>(null);
    const [sophosMissing, setSophosMissing] = useState<boolean | null>(null);
    const [threatLockerMissing, setThreatLockerMissing] = useState<boolean | null>(null);
    const [bitLockerMissing, setBitLockerMissing] = useState<boolean | null>(null);
    const [pulsewayPortalMissing, setPulsewayPortalMissing] = useState<boolean | null>(null);
    const [sophosCentralMissing, setSophosCentralMissing] = useState<boolean | null>(null);
    const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
    const [selectedFields, setSelectedFields] = useState<string[]>(DEVICE_TABLE_DEFAULT_FIELDS);
    const [availableFields, setAvailableFields] = useState<string[]>([]);

    const { data: devicesData, isLoading, error, refetch } = useQuery<ApiResponse<Device>, Error>({
        queryKey: ['devices', page, rowsPerPage, JSON.stringify(filters)],
        queryFn: () => fetchDevices(page + 1, rowsPerPage, filters),
        staleTime: 5000,
        gcTime: 300000,
        refetchOnWindowFocus: false,
    });

    const { data: sitesData } = useQuery<ApiResponse<Site>, Error>({
        queryKey: ['sites'],
        queryFn: fetchSites,
        staleTime: 5000,
        gcTime: 300000,
        refetchOnWindowFocus: false,
    });

    // Update availableFields when data is loaded
    useEffect(() => {
        if (devicesData?.data.data && devicesData.data.data.length > 0) {
            setAvailableFields(Object.keys(devicesData.data.data[0]).sort());
        }
    }, [devicesData]);

    // Update filters when missing status changes
    useEffect(() => {
        setFilters(prev => ({
            ...prev,
            PulsewayMissing: pulsewayMissing !== null ? (pulsewayMissing ? '1' : '0') : undefined,
            SophosMissing: sophosMissing !== null ? (sophosMissing ? '1' : '0') : undefined,
            ThreatLockerMissing: threatLockerMissing !== null ? (threatLockerMissing ? '1' : '0') : undefined,
            BitLockerMissing: bitLockerMissing !== null ? (bitLockerMissing ? '1' : '0') : undefined,
            PulsewayPortalMissing: pulsewayPortalMissing !== null ? (pulsewayPortalMissing ? '1' : '0') : undefined,
            SophosCentralMissing: sophosCentralMissing !== null ? (sophosCentralMissing ? '1' : '0') : undefined,
        }));
    }, [pulsewayMissing, sophosMissing, threatLockerMissing, bitLockerMissing, pulsewayPortalMissing, sophosCentralMissing]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleFilterChange = (key: keyof FilterParams, value: string) => {
        setFilters(prev => ({
            ...prev,
            [key]: value || undefined
        }));
        setPage(0);
    };

    const applySearchFilters = () => {
        setFilters(prev => ({
            ...prev,
            NetworkComputerName: searchComputerName || undefined,
            SystemSerialNumber: searchSerialNumber || undefined,
            OsLastLoggedOnUser: searchLastUser || undefined,
        }));
        setPage(0);
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            applySearchFilters();
        }
    };

    const handleExportDialogOpen = () => {
        setIsExportDialogOpen(true);
    };

    const handleExportDialogClose = () => {
        setIsExportDialogOpen(false);
    };

    const handleExportCSV = async () => {
        try {
            setIsExporting(true);
            const allData = await fetchAllDevicesForExport(filters);
            await exportToCSV(allData.data.data, 'devices.csv', { fields: selectedFields });
        } catch (err) {
            // Handle error (optional toast)
        } finally {
            setIsExporting(false);
            setIsExportDialogOpen(false);
        }
    };

    const handleCopy = async (text: string, field: string) => {
        await navigator.clipboard.writeText(text);
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 2000); // Reset after 2 seconds
    };

    const handleMissingStatusChange = (
        field: 'PulsewayMissing' | 'SophosMissing' | 'ThreatLockerMissing' | 'BitLockerMissing' | 'PulsewayPortalMissing' | 'SophosCentralMissing',
        setter: React.Dispatch<React.SetStateAction<boolean | null>>,
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const checked = event.target.checked;
        setter(checked ? true : null);
        handleFilterChange(field, checked ? '1' : '');
    };

    const handleClearFilters = () => {
        setFilters({});
        setPage(0);
        setPulsewayMissing(null);
        setSophosMissing(null);
        setThreatLockerMissing(null);
        setBitLockerMissing(null);
        setPulsewayPortalMissing(null);
        setSophosCentralMissing(null);
        setSearchComputerName('');
        setSearchSerialNumber('');
        setSearchLastUser('');
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={2}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error.message}
                </Alert>
                <Button startIcon={<RefreshIcon />} onClick={() => refetch()}>
                    Retry
                </Button>
            </Box>
        );
    }

    const totalRecords = devicesData?.data.pagination.totalRecords || 0;

    return (
        <Paper sx={{ 
            width: '100%', 
            height: 'calc(100vh - 140px)', // Reduced height by increasing the subtracted pixels
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'hidden' // Prevent outer paper from scrolling
        }}>
            <Box 
                sx={{ 
                    p: 2, 
                    display: 'flex', 
                    gap: 2, 
                    alignItems: 'center', 
                    flexWrap: 'wrap',
                    backgroundColor: 'background.paper',
                    borderBottom: 1,
                    borderColor: 'divider',
                    zIndex: 2
                }}
            >
                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Site Name</InputLabel>
                    <Select
                        value={filters.SiteName || ''}
                        onChange={(e) => handleFilterChange('SiteName', e.target.value)}
                        label="Site Name"
                    >
                        <MenuItem value="">All</MenuItem>
                        {sitesData?.data.data.map((site: Site) => (
                            <MenuItem key={site.SiteName} value={site.SiteName}>
                                {site.SiteName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>OS Version</InputLabel>
                    <Select
                        value={filters.OsVersion || ''}
                        onChange={(e) => handleFilterChange('OsVersion', e.target.value)}
                        label="OS Version"
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="Windows 7">Windows 7</MenuItem>
                        <MenuItem value="Windows 10">Windows 10</MenuItem>
                        <MenuItem value="Windows 11">Windows 11</MenuItem>
                        <MenuItem value="Windows Server 2019">Windows Server 2019</MenuItem>
                        <MenuItem value="Windows Server 2016">Windows Server 2016</MenuItem>
                        <MenuItem value="Windows Server 2022">Windows Server 2022</MenuItem>
                    </Select>
                </FormControl>

                <TextField
                    label="Search Computer Name"
                    variant="outlined"
                    value={searchComputerName}
                    onChange={(e) => setSearchComputerName(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    sx={{ minWidth: 200 }}
                />

                <TextField
                    label="Search Serial Number"
                    variant="outlined"
                    value={searchSerialNumber}
                    onChange={(e) => setSearchSerialNumber(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    sx={{ minWidth: 200 }}
                />

                <TextField
                    label="Search Last Logged User"
                    variant="outlined"
                    value={searchLastUser}
                    onChange={(e) => setSearchLastUser(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    sx={{ minWidth: 200 }}
                />

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={pulsewayMissing === true}
                                onChange={(e) => handleMissingStatusChange('PulsewayMissing', setPulsewayMissing, e)}
                                indeterminate={pulsewayMissing === null}
                            />
                        }
                        label="Pulseway Missing"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={sophosMissing === true}
                                onChange={(e) => handleMissingStatusChange('SophosMissing', setSophosMissing, e)}
                                indeterminate={sophosMissing === null}
                            />
                        }
                        label="Sophos Missing"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={threatLockerMissing === true}
                                onChange={(e) => handleMissingStatusChange('ThreatLockerMissing', setThreatLockerMissing, e)}
                                indeterminate={threatLockerMissing === null}
                            />
                        }
                        label="ThreatLocker Missing"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={bitLockerMissing === true}
                                onChange={(e) => handleMissingStatusChange('BitLockerMissing', setBitLockerMissing, e)}
                                indeterminate={bitLockerMissing === null}
                            />
                        }
                        label="BitLocker Missing"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={pulsewayPortalMissing === true}
                                onChange={(e) => handleMissingStatusChange('PulsewayPortalMissing', setPulsewayPortalMissing, e)}
                                indeterminate={pulsewayPortalMissing === null}
                            />
                        }
                        label="Pulseway Portal Missing"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={sophosCentralMissing === true}
                                onChange={(e) => handleMissingStatusChange('SophosCentralMissing', setSophosCentralMissing, e)}
                                indeterminate={sophosCentralMissing === null}
                            />
                        }
                        label="Sophos Central Missing"
                    />
                </Box>

                <Stack direction="row" spacing={2} sx={{ ml: 'auto' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<RefreshIcon />}
                        onClick={applySearchFilters}
                        disabled={isLoading}
                    >
                        Search
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<ClearIcon />}
                        onClick={handleClearFilters}
                    >
                        Clear Filters
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<RefreshIcon />}
                        onClick={() => refetch()}
                        disabled={isLoading}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleExportDialogOpen}
                        disabled={isExporting}
                    >
                        Export to CSV
                    </Button>
                </Stack>
            </Box>

            <TableContainer 
                sx={{ 
                    flex: 1, 
                    overflow: 'auto', // Enable scrolling for the table container
                    backgroundColor: 'background.paper'
                }}
            >
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ minWidth: 60, backgroundColor: 'background.paper' }}>Actions</TableCell>
                            <TableCell sx={{ minWidth: 180, backgroundColor: 'background.paper' }}>Site Name</TableCell>
                            <TableCell sx={{ minWidth: 220, backgroundColor: 'background.paper' }}>Device Name</TableCell>
                            <TableCell sx={{ minWidth: 140, backgroundColor: 'background.paper' }}>IP Address</TableCell>
                            <TableCell sx={{ minWidth: 200, backgroundColor: 'background.paper' }}>Manufacturer</TableCell>
                            <TableCell sx={{ minWidth: 200, backgroundColor: 'background.paper' }}>Serial Number</TableCell>
                            <TableCell sx={{ minWidth: 200, backgroundColor: 'background.paper' }}>OS Version</TableCell>
                            <TableCell sx={{ minWidth: 220, backgroundColor: 'background.paper' }}>Last Logged On User</TableCell>
                            <TableCell sx={{ minWidth: 120, backgroundColor: 'background.paper' }}>Laptop</TableCell>
                            <TableCell sx={{ minWidth: 140, backgroundColor: 'background.paper' }}>Online</TableCell>
                            <TableCell sx={{ minWidth: 220, backgroundColor: 'background.paper' }}>Hardware Readiness</TableCell>
                            <TableCell sx={{ minWidth: 220, backgroundColor: 'background.paper' }}>Warranty Status</TableCell>
                            <TableCell sx={{ minWidth: 180, backgroundColor: 'background.paper' }}>Pulseway</TableCell>
                            <TableCell sx={{ minWidth: 180, backgroundColor: 'background.paper' }}>Sophos</TableCell>
                            <TableCell sx={{ minWidth: 180, backgroundColor: 'background.paper' }}>ThreatLocker</TableCell>
                            <TableCell sx={{ minWidth: 160, backgroundColor: 'background.paper' }}>BitLocker</TableCell>
                            <TableCell sx={{ minWidth: 160, backgroundColor: 'background.paper' }}>Last Updated</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {devicesData?.data.data.map((device: Device, index: number) => (
                            <TableRow 
                                key={device.DeviceId || `device-${index}`}
                                hover
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                                <TableCell>
                                    <Tooltip title="View Details">
                                        <IconButton size="small" onClick={() => { setSelectedDevice(device); setDialogOpen(true); }}>
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>{device.SiteName}</TableCell>
                                <TableCell>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <span>{device.NetworkComputerName}</span>
                                        <Tooltip title={copiedField === `name-${index}` ? 'Copied!' : 'Copy name'}>
                                            <IconButton size="small" onClick={() => handleCopy(device.NetworkComputerName, `name-${index}`)}>
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <span>{device.NetworkIpAddress}</span>
                                        <Tooltip title={copiedField === `ip-${index}` ? 'Copied!' : 'Copy IP'}>
                                            <IconButton size="small" onClick={() => handleCopy(device.NetworkIpAddress, `ip-${index}`)}>
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                                <TableCell>{device.SystemManufacturer}</TableCell>
                                <TableCell>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <span>{device.SystemSerialNumber}</span>
                                        <Tooltip title={copiedField === `serial-${index}` ? 'Copied!' : 'Copy serial'}>
                                            <IconButton size="small" onClick={() => handleCopy(device.SystemSerialNumber, `serial-${index}`)}>
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                                <TableCell>{device.OsVersion}</TableCell>
                                <TableCell>{device.OsLastLoggedOnUser}</TableCell>
                                <TableCell>{device.SystemLaptop ? 'Yes' : 'No'}</TableCell>
                                <TableCell>{device.DeviceOnline}</TableCell>
                                <TableCell>{device.HardwareReadinessResult}</TableCell>
                                <TableCell>{device.WarrantyStatus}</TableCell>
                                <TableCell>{device.PulsewayMissing ? 'Missing' : 'Installed'}</TableCell>
                                <TableCell>{device.SophosMissing ? 'Missing' : 'Installed'}</TableCell>
                                <TableCell>{device.ThreatLockerMissing ? 'Missing' : 'Installed'}</TableCell>
                                <TableCell>{device.BitLockerMissing ? 'Missing' : 'Installed'}</TableCell>
                                <TableCell>{device.LastUpdated}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ p: 1, backgroundColor: 'background.paper' }}>
                <TablePagination
                    component="div"
                    count={totalRecords}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    rowsPerPageOptions={[100, 200, 500, 1000]}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Box>

            <DeviceDialog
                device={selectedDevice}
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
            />

            <ExportDialog
                open={isExportDialogOpen}
                onClose={handleExportDialogClose}
                availableFields={availableFields}
                selectedFields={selectedFields}
                setSelectedFields={(updater) => setSelectedFields(prev => updater(prev))}
                onExport={handleExportCSV}
                isExporting={isExporting}
                visibleDefaultFields={DEVICE_TABLE_DEFAULT_FIELDS}
                ignoreFields={DEFAULT_EXPORT_DEVICE_IGNORE_FIELDS}
            />
        </Paper>
    );
}; 